import React, { FC, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
  makeStyles,
  ThemeProvider,
} from "@material-ui/core";
import { createTheme } from "@material-ui/core/styles";
import {
  Dashboard as DashboardIcon,
  LibraryMusic as LibraryMusicIcon,
  CloudUpload as UploadIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Assessment as AnalyticsIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  School as OnboardingIcon,
} from "@material-ui/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useTokenManager } from "../../contexts/TokenManagerContext";

const drawerWidth = 260;

const artistTheme = createTheme({
  palette: {
    type: "dark",
    primary: { main: "#578fb7" },
    secondary: { main: "#c2185b" },
    background: { default: "#02172a", paper: "#1a2332" },
  },
  overrides: {
    MuiDrawer: {
      paper: {},
    },
  },
});

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
    boxSizing: "border-box",
    backgroundColor: "#132f44",
    borderRight: "1px solid #2a4a5e",
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(2),
    marginTop: 64,
    minHeight: "calc(100vh - 64px)",
    backgroundColor: "#02172a",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: "#0d2840",
    borderBottom: "1px solid #2a4a5e",
  },
}));

interface ArtistLayoutProps {
  children: React.ReactNode;
}

const artistNavItems: { label: string; path: string; icon: React.ReactNode }[] = [
  { label: "Earnings & Plays", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "MusicHub", path: "/artist/dashboard", icon: <LibraryMusicIcon /> },
  { label: "Upload", path: "/upload", icon: <UploadIcon /> },
  { label: "Profile", path: "/profile", icon: <PersonIcon /> },
  { label: "Earnings", path: "/earnings", icon: <MoneyIcon /> },
  { label: "Analytics", path: "/analytics", icon: <AnalyticsIcon /> },
  { label: "Onboarding", path: "/artist/onboarding", icon: <OnboardingIcon /> },
];

/** Beneficiary-only session (listener / streamer): same plays & balance surface, no artist tools. */
const streamerNavItems: { label: string; path: string; icon: React.ReactNode }[] = [
  { label: "Home", path: "/dashboard", icon: <DashboardIcon /> },
  { label: "Profile", path: "/profile", icon: <PersonIcon /> },
];

const ArtistLayout: FC<ArtistLayoutProps> = ({ children }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const tokenManager = useTokenManager();

  const isStreamerShell =
    tokenManager.isBeneficiary &&
    !tokenManager.isArtist &&
    localStorage.getItem("isAdmin") !== "true";

  const navItems = useMemo(
    () => (isStreamerShell ? streamerNavItems : artistNavItems),
    [isStreamerShell]
  );

  useEffect(() => {
    document.title = isStreamerShell
      ? "StarFarm - Streamer"
      : "StarFarm - Artist Portal";
  }, [isStreamerShell]);

  const handleNav = (path: string) => {
    history.push(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleLogout = () => {
    tokenManager.clearToken();
    localStorage.removeItem("user");
    localStorage.removeItem("impersonating");
    localStorage.removeItem("impersonatedArtistId");
    window.location.href = `${window.location.origin}/login`;
  };

  const isActive = (path: string) => {
    if (path === "/dashboard") {
      return ["/beneficiary", "/dashboard", "/"].includes(location.pathname);
    }
    return (
      location.pathname === path ||
      location.pathname.startsWith(path + "/")
    );
  };

  const currentTitle =
    navItems.find((n) => isActive(n.path))?.label ?? "Artist";

  /** Same earnings dashboard as /dashboard; /beneficiary redirects here but may still appear briefly */
  const isBeneficiaryPage = ["/beneficiary", "/dashboard", "/"].includes(
    location.pathname
  );

  const drawer = (
    <div>
      <Box p={2} display="flex" flexDirection="column" alignItems="flex-start">
        <Typography variant="h6" style={{ color: "#7eb8d6", fontWeight: 700 }}>
          StarFarm
        </Typography>
        <Typography variant="caption" style={{ color: "#8fa8bc", marginTop: 4 }}>
          {isStreamerShell ? "Streamer portal" : "Artist portal"}
        </Typography>
      </Box>
      <Divider style={{ backgroundColor: "#2a4a5e" }} />
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.path}
            onClick={() => handleNav(item.path)}
            selected={isActive(item.path)}
            style={{
              backgroundColor: isActive(item.path)
                ? "rgba(87, 143, 183, 0.2)"
                : "transparent",
            }}
          >
            <ListItemIcon style={{ color: isActive(item.path) ? "#7eb8d6" : "#b0c4d4", minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                style: {
                  color: isActive(item.path) ? "#e8f4fc" : "#c5d5e0",
                  fontSize: "0.95rem",
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      <Divider style={{ backgroundColor: "#2a4a5e" }} />
      <List>
        <ListItem button onClick={handleLogout}>
          <ListItemIcon style={{ color: "#b0c4d4", minWidth: 40 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText primary="Log out" style={{ color: "#c5d5e0" }} />
        </ListItem>
      </List>
    </div>
  );

  return (
    <ThemeProvider theme={artistTheme}>
      <Box
        display="flex"
        style={{
          minHeight: "100vh",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          backgroundColor: "#02172a",
          overflowX: "hidden",
        }}
      >
        <AppBar
          position="fixed"
          className={classes.appBar}
          style={{
            width: isMobile ? "100%" : `calc(100% - ${drawerWidth}px)`,
            marginLeft: isMobile ? 0 : drawerWidth,
          }}
        >
          <Toolbar>
            {isMobile ? (
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileOpen(true)}
                style={{ marginRight: theme.spacing(2) }}
              >
                <MenuIcon />
              </IconButton>
            ) : null}
            <Typography variant="h6" noWrap style={{ flexGrow: 1, color: "#e8f4fc" }}>
              {currentTitle}
            </Typography>
          </Toolbar>
        </AppBar>

        <nav className={classes.drawer}>
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={() => setMobileOpen(false)}
              classes={{ paper: classes.drawerPaper }}
              ModalProps={{ keepMounted: true }}
            >
              {drawer}
            </Drawer>
          ) : (
            <Drawer variant="permanent" open classes={{ paper: classes.drawerPaper }}>
              {drawer}
            </Drawer>
          )}
        </nav>

        <Box
          component="main"
          className={classes.content}
          style={{
            width: isMobile ? "100%" : undefined,
            flex: isMobile ? undefined : "1 1 0%",
            minWidth: 0,
            marginLeft: 0,
            overflowX: "hidden",
            ...(isBeneficiaryPage
              ? {
                  display: "flex",
                  flexDirection: "column",
                  height: "calc(100vh - 64px)",
                  maxHeight: "calc(100vh - 64px)",
                  overflow: "hidden",
                  padding: 0,
                }
              : {}),
          }}
        >
          {children}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ArtistLayout;
